<script setup>
import {computed} from "vue";
import DataTable from "@/components/global-themed/table/DataTable.vue";
import TableHeaderRow from "@/components/global-themed/table/TableHeaderRow.vue";
import TableHeaderCell from "@/components/global-themed/table/TableHeaderCell.vue";
import TableBody from "@/components/global-themed/table/TableBody.vue";
import DataRow from "@/components/reporting/partials/DataRow.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import ErrorBlock from "@/components/reporting/partials/ErrorBlock.vue";
import ReportingEmpty from "@/components/reporting/partials/ReportingEmpty.vue";
import ConnectAccount from "@/components/reporting/partials/ConnectAccount.vue";
import Reporting from "@/services/reporting";
import {useQueries} from "@tanstack/vue-query";
import useTrafficStore from "@/composables/useTrafficStore";
import useQueriesState from "@/composables/useQueriesState";
import useAccountMissing from "@/composables/useAccountMissing";
import useOrderBy from "@/composables/useOrderBy";

const {orderBy, orderDirection, orderChanged, orderData} = useOrderBy('value');

const {fromDate, toDate, compareFromDate, compareToDate} = useTrafficStore();

const {isAccountMissing, setIsAccountMissing, checkResponseForAccountMissing} = useAccountMissing();

const fetchSources = async (fromDate, toDate) => {
  if (!fromDate || !toDate) return null;
  setIsAccountMissing(false);
  try {
    return (await Reporting.fetchReferrers(fromDate, toDate)).data.data;
  } catch (e) {
    checkResponseForAccountMissing(e.response);
    throw e;
  }
}

const results = useQueries({
  queries: [
    {
      queryKey: ['reporting-referrers', 'reporting', fromDate, toDate],
      queryFn: async () => fetchSources(fromDate.value, toDate.value),
    },
    {
      queryKey: ['reporting-referrers', 'reporting', compareFromDate, compareToDate],
      queryFn: async () => fetchSources(compareFromDate.value, compareToDate.value),
    },
  ],
});

const {data, comparedData, isLoading: loading, isError} = useQueriesState(results);

const orderedData = computed(() => orderData(data.value).slice(0, 5));

</script>

<template>
  <LoadingBlock v-if="loading" />
  <ConnectAccount v-else-if="isAccountMissing" />
  <ErrorBlock v-else-if="isError" />
  <ReportingEmpty v-else-if="!loading && !data.length" />
  <DataTable v-else>
    <TableHeaderRow class="tw-px-0 tw-mx-0">
      <TableHeaderCell
        :order-by="orderBy"
        :order-direction="orderDirection"
        name="referrer"
        class="tw-col-span-6"
        @order-changed="orderChanged"
      >
        Source
      </TableHeaderCell>

      <TableHeaderCell
        :order-by="orderBy"
        :order-direction="orderDirection"
        name="value"
        class="tw-col-span-6 tw-justify-end"
        @order-changed="orderChanged"
      >
        Views
      </TableHeaderCell>
    </TableHeaderRow>
    <TableBody class="!tw-py-0 !tw-border-b-0">
      <DataRow
        v-for="row in orderedData"
        :key="row.referrer"
        class="!tw-border-0"
        icon="link"
        :label="row.referrer"
        :value="row.value"
        :compare-value="comparedData?.find(compare => compare.referrer === row.referrer)?.value"
      />
    </TableBody>
  </DataTable>
</template>